import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { formatAIChatTime } from '../../components/Succession/Messaging/shared/dateUtils';
import { Link, useParams } from 'react-router-dom';
import classNames from 'classnames';
import SearchBar from '../../../../ui/canon/SearchBar.jsx';
import Button from '../../../../ui/canon/Button.jsx';

// Simple Lucide-like icons using @heroicons which we already ship
import { PlusIcon, ChatBubbleLeftRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export default function ConversationSidebar({ conversations, onNewConversation, className }) {
  const intl = useIntl();
  const { conversationId } = useParams();
  const [query, setQuery] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchContainerRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) => c.title.toLowerCase().includes(q) || c.preview.toLowerCase().includes(q)
    );
  }, [conversations, query]);

  // Handle clicking outside search to collapse when empty
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target) &&
        !query.trim()
      ) {
        setIsSearchExpanded(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && !query.trim()) {
        setIsSearchExpanded(false);
      }
    };

    if (isSearchExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isSearchExpanded, query]);

  // Focus the search input when expanded
  useEffect(() => {
    if (isSearchExpanded && searchContainerRef.current) {
      const input = searchContainerRef.current.querySelector('input');
      if (input) input.focus();
    }
  }, [isSearchExpanded]);

  const handleSearchIconClick = () => {
    setIsSearchExpanded(true);
  };

  return (
    <div className={classNames('flex flex-col h-full min-h-0 bg-succession-bg-primary border-r border-succession-border', className)}>
      <div className="p-4 border-b border-succession-border space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-succession-text-primary m-0 p-0">{intl.formatMessage({ id: 'assistant.sidebar.heading' })}</h2>
          <div className="flex items-center gap-1">
            {!isSearchExpanded && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-2"
                onClick={handleSearchIconClick}
                title={intl.formatMessage({ id: 'assistant.sidebar.search_title' })}
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
              </Button>
            )}
            <Button as={Link} to="/assistant" size="sm" className="h-8 px-2" onClick={onNewConversation} title={intl.formatMessage({ id: 'assistant.sidebar.new_conversation_title' })}>
              <PlusIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {isSearchExpanded && (
          <div ref={searchContainerRef}>
            <SearchBar
              value={query}
              onChange={setQuery}
              onSubmit={() => {}}
              placeholder={intl.formatMessage({ id: 'assistant.sidebar.search_placeholder' })}
              variant="outline"
              size="md"
            />
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-2 space-y-1">
          {filtered.length === 0 ? (
            <div className="p-4 text-center">
              <p className="text-sm text-succession-text-secondary">{query ? intl.formatMessage({ id: 'assistant.sidebar.no_results' }) : intl.formatMessage({ id: 'assistant.sidebar.empty' })}</p>
            </div>
          ) : (
            filtered.map((c) => (
              <Link
                key={c.id}
                to={`/assistant/${c.id}`}
                className={classNames(
                  'block p-3 rounded-lg transition-colors',
                  'hover:bg-succession-bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring',
                  conversationId === c.id ? 'bg-succession-bg-secondary' : 'bg-transparent'
                )}
              >
                <div className="flex items-start gap-3">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 text-succession-text-secondary mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-succession-text-primary truncate m-0 p-0">{c.title}</p>
                    <p className="text-xs text-succession-text-secondary truncate mt-1">{c.preview}</p>
                    <p className="text-xs text-succession-text-secondary mt-1">{(() => {
                      const formatted = formatAIChatTime(c.timestamp);
                      return formatted === 'Invalid date' ? c.timestamp : formatted;
                    })()}</p>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}


